import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

const STATS = [
  { value: 5, suffix: "", label: "Government schools supported" },
  { value: 500, suffix: "+", label: "Lives touched" },
  { value: 1300, suffix: "+", label: "Students cyber-safety trained" },
  { value: 1500, suffix: "+", label: "Donors & supporters" },
];

const Counter = ({ to, suffix }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    const dur = 1800;
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      setVal(Math.round(to * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to]);

  return (
    <span ref={ref}>
      {val.toLocaleString()}
      {suffix}
    </span>
  );
};

export const Impact = () => (
  <section id="impact" className="py-24 lg:py-32 bg-forest text-bone relative overflow-hidden" data-testid="impact-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7 }}
        className="mb-16 lg:mb-20"
      >
        <span className="text-xs font-bold tracking-[0.3em] uppercase text-terra" data-testid="impact-eyebrow">Impact, Measured in Light</span>
        <h2 className="font-serif font-bold tracking-tighter text-4xl sm:text-5xl lg:text-6xl mt-4 max-w-3xl" data-testid="impact-heading">
          Small lamps. <span className="italic text-terra">Enormous</span> glow.
        </h2>
      </motion.div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 border border-bone/20 divide-y sm:divide-y-0 lg:divide-x divide-bone/20">
        {STATS.map((s, i) => (
          <motion.div
            key={s.label}
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: i * 0.1 }}
            className="p-8 lg:p-10 sm:border-l sm:first:border-l-0 lg:border-l-0 border-bone/20"
            data-testid={`impact-stat-${i}`}
          >
            <p className="font-serif font-bold text-5xl lg:text-6xl tracking-tighter text-bone">
              <Counter to={s.value} suffix={s.suffix} />
            </p>
            <p className="mt-3 text-sm font-semibold tracking-wide uppercase text-bone/60">{s.label}</p>
          </motion.div>
        ))}
      </div>

      <motion.p
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 0.3 }}
        className="mt-12 max-w-2xl text-base leading-relaxed text-bone/70"
        data-testid="impact-note"
      >
        Figures from our 2025 annual report, independently reviewed. Every number is a
        child who can now read this sentence.
      </motion.p>
    </div>
    <span className="font-serif italic font-black outline-text text-[16rem] absolute -bottom-16 -right-8 opacity-40 select-none pointer-events-none hidden lg:block" aria-hidden="true">
      light
    </span>
  </section>
);
